import React, { FC, useEffect, useState } from 'react';
import { Control, FieldErrors, UseFormRegister, UseFormSetValue, useWatch } from 'react-hook-form';

import { ButtonToggles, RadioButtons } from '@eika/radio-button';

import { SFOWeb, ValgtWeb } from '../../generated-types';
import { useTranslation } from '../../translations';
import { BarnFormData } from './barn';

interface Props {
    index: number;
    register: UseFormRegister<BarnFormData>;
    control: Control<BarnFormData>;
    setValue: UseFormSetValue<BarnFormData>;
    errors: FieldErrors<BarnFormData>;
}

const Sfo: FC<Props> = ({ index, register, control, setValue, errors }) => {
    const { barn, button, validationMessage } = useTranslation();
    const value = useWatch({ name: 'barn', control });
    const [harSfoPlass, setHarSfoPlass] = useState<ValgtWeb>(value?.[index]?.harSfoPlass);
    const [sfoPlass, setSfoPlass] = useState<SFOWeb>(value?.[index]?.sfoPlass);

    useEffect((): void => {
        register(`barn.${index}.harSfoPlass` as const, {
            validate: { ikkeValgt: (value) => value !== ValgtWeb.IKKE_VALGT },
        });
        register(`barn.${index}.sfoPlass` as const, {
            validate: { ikkeValgt: (value) => value !== SFOWeb.IKKE_VALGT },
        });
    }, [register, index, validationMessage]);

    const onChangeHarSfoPlass = (value: ValgtWeb) => {
        setValue(`barn.${index}.harSfoPlass`, value, {
            shouldValidate: true,
        });
        if (value === ValgtWeb.NEI) {
            setValue(`barn.${index}.sfoPlass`, SFOWeb.INGEN);
        }
        setHarSfoPlass(value);
    };

    const onChangeSfoPlass = (value: SFOWeb) => {
        setValue(`barn.${index}.sfoPlass`, value, {
            shouldValidate: true,
        });
        setSfoPlass(value);
    };

    return (
        <>
            <ButtonToggles
                label={barn.togglesSfoUtgifter}
                data-track-id="kreditt-boliglaan-barn-valg-har-sfo-plass"
                data-track-linkname={value}
                onChange={(value) => onChangeHarSfoPlass(value as ValgtWeb)}
                selectedOption={harSfoPlass}
                options={[
                    { label: button.ja, value: ValgtWeb.JA },
                    { label: button.nei, value: ValgtWeb.NEI },
                ]}
                errorMessage={
                    errors.barn?.[index]?.harSfoPlass?.type === 'ikkeValgt' ? `${validationMessage.requiredChoose}` : ''
                }
            />
            {harSfoPlass === ValgtWeb.JA && (
                <RadioButtons
                    label={barn.radioTitleSfo}
                    options={[
                        {
                            value: SFOWeb.DELTID,
                            label: barn.radioDeltid,
                        },
                        {
                            value: SFOWeb.HELTID,
                            label: barn.radioHeltid,
                        },
                    ]}
                    selectedOption={sfoPlass}
                    errorMessage={
                        errors.barn?.[index]?.sfoPlass?.type === 'ikkeValgt'
                            ? `${validationMessage.requiredChoose}`
                            : ''
                    }
                    onChange={(value) => onChangeSfoPlass(value as SFOWeb)}
                />
            )}
        </>
    );
};

export default Sfo;
